import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";
import api from "@/lib/api";
import { errText } from "@/components/pageSettings/AdminModal";
import { SetupSteps } from "@/components/buyersetup/SetupSteps";
import { CompanyStep } from "@/components/buyersetup/CompanyStep";
import { CriteriaStep } from "@/components/buyersetup/CriteriaStep";
import { PagesStep } from "@/components/buyersetup/PagesStep";
import { DeliveryStep } from "@/components/buyersetup/DeliveryStep";
import { GoLiveStep } from "@/components/buyersetup/GoLiveStep";
import { setupUrl } from "@/components/buyersetup/StepShell";

const VIEWS = { company: CompanyStep, criteria: CriteriaStep, pages: PagesStep, delivery: DeliveryStep, live: GoLiveStep };

export default function BuyerSetup() {
  const { id } = useParams();
  const navigate = useNavigate();
  const carry = useLocation().state || {};
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState(null);
  useEffect(() => {
    setState(null);
    window.scrollTo(0, 0);
    if (id) api.get(setupUrl(id)).then((r) => setState(r.data)).catch((e) => toast.error(errText(e, "Couldn't load this buyer")));
  }, [id]);
  const step = !id ? "company" : params.get("step") || state?.steps.find((s) => !s.done)?.key || "live";
  const go = (key) => { setParams({ step: key }, { state: carry }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const onSaved = (data, next) => { setState(data); if (next) go(next); };
  const onCreated = (data) => navigate(`/clients/${data.org.id}/setup?step=criteria`, { state: { prefill: carry.prefill } });
  const View = VIEWS[step] || CompanyStep;
  return (
    <div className="p-4 md:p-8 pb-24 md:pb-8" data-testid="buyer-setup-page">
      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" onClick={() => navigate(id ? `/clients/${id}/manage` : "/clients")} data-testid="buyer-setup-back"
                className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1">
          <ArrowLeft size={10} weight="bold" /> {id ? "Manage buyer" : "Clients"}
        </button>
        <div>
          <div className="label-mono">Set up a buyer</div>
          <h1 className="font-chivo font-black text-3xl md:text-4xl uppercase tracking-tighter leading-none" data-testid="buyer-setup-title">
            {state?.org.name || (id ? "Loading…" : "New buyer")}
          </h1>
        </div>
        {state && (
          <span className={`ml-auto border-2 border-black px-2 py-1 text-[10px] uppercase tracking-widest ${state.live ? "bg-emerald-600 text-white" : "bg-zinc-200"}`} data-testid="buyer-setup-status">
            {state.live ? "Live" : "Not live yet"}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 md:gap-8 mt-6">
        <SetupSteps steps={state?.steps} current={step} onPick={go} />
        <div className="min-w-0">
          {id && !state
            ? <div className="label-mono p-6" data-testid="buyer-setup-loading">Loading…</div>
            : <View key={step} state={state} orgId={id} onSaved={onSaved} onCreated={onCreated} onPick={go} carry={carry} />}
        </div>
      </div>
    </div>
  );
}
